import { gql } from 'urql';
import { NoteReceivableFragment, OwnerFragment } from './fragments';
import {
  CloseNoteReceivableInput,
  Mutation,
  Query,
  QueryNoteReceivableArgs,
  UpdateNoteReceivableInput,
} from '../generated/graphql';
import { GraphQLClient } from 'graphql-request';
import { MonetaryAmountFragment, OwnershipFragment, MonetaryAmountDataPointGroupFragment } from './fragments';

export const deleteNoteReceivableMutation = gql`
  mutation deleteNoteReceivable($input: DeleteNoteReceivableInput!) {
    deleteNoteReceivable(input: $input) {
      changeToken
    }
  }
`;

export const getNoteReceivable = async (client: GraphQLClient, args: QueryNoteReceivableArgs) => {
  return (
    await client.request<Pick<Query, 'noteReceivable'>>(
      gql`
        ${OwnerFragment}
        ${MonetaryAmountFragment}
        query GetNoteReceivable($householdID: ID!, $id: ID!) {
          noteReceivable(householdID: $householdID, id: $id) {
            __typename
            id
            householdID
            name
            changeToken
            notes
            closedAt
            ownedValue {
              ...MonetaryAmountFragment
            }
            ownership {
              __typename
              ... on TenantsInCommon {
                interests {
                  owner {
                    ...OwnerFragment
                  }
                  percentage
                }
              }
            }
            value {
              tenantID
              id
              updatedAt
              latestDataPoint {
                data {
                  __typename
                  ... on MonetaryAmountDataPointValue {
                    value {
                      currencyCode
                      value
                    }
                  }
                }
              }
              dataPoints {
                items {
                  id
                  dateTime
                  data {
                    __typename
                    ... on MonetaryAmountDataPointValue {
                      value {
                        currencyCode
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
      {
        ...args,
      }
    )
  ).noteReceivable;
};

export const getNoteReceivablesQuery = gql`
  ${MonetaryAmountFragment}
  query GetNoteReceivables($householdID: ID!) {
    noteReceivables(householdID: $householdID) {
      items {
        id
        householdID
        name
        ownership {
          __typename
          ... on TenantsInCommon {
            interests {
              percentage
            }
          }
        }
        ownedValue {
          ...MonetaryAmountFragment
        }
        value {
          tenantID
          id
          updatedAt
          latestDataPoint {
            id
            dateTime
            data {
              __typename
              ... on MonetaryAmountDataPointValue {
                value {
                  __typename
                  currencyCode
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const createNoteReceivableMutation = gql`
  ${OwnershipFragment}
  ${MonetaryAmountDataPointGroupFragment}
  ${NoteReceivableFragment}
  mutation createNoteReceivable($input: CreateNoteReceivableInput!) {
    createNoteReceivable(input: $input) {
      noteReceivable {
        __typename
        id
        householdID
        createdAt
        updatedAt
        changeToken
        name
        notes
        ownedValue {
          __typename
          value
          currencyCode
        }
        owners {
          __typename
          id
          ownerType
          ... on Person {
            givenName
            familyName
          }
          ... on Business {
            name
          }
        }
        ownership {
          ...OwnershipFragment
        }
        value {
          ...MonetaryAmountDataPointGroupFragment
        }
      }
    }
  }
`;

export type { CloseNoteReceivableInput };

export const closeNoteReceivableMutation = gql`
  mutation CloseNoteReceivable($input: CloseNoteReceivableInput!) {
    closeNoteReceivable(input: $input) {
      changeToken
    }
  }
`;

export const clientCloseNoteReceivable = async (client: GraphQLClient, input: CloseNoteReceivableInput) => {
  return (
    await client.request<{ closeNoteReceivable: { changeToken: string } }>(closeNoteReceivableMutation, {
      input,
    })
  ).closeNoteReceivable;
};

export const mergeNoteReceivablesMutation = gql`
  mutation MergeNoteReceivables($input: MergeNoteReceivablesInput!) {
    mergeNoteReceivables(input: $input) {
      merged
    }
  }
`;

const updateNoteReceivableMutation = gql`
  ${NoteReceivableFragment}
  mutation UpdateNoteReceivable($input: UpdateNoteReceivableInput!) {
    updateNoteReceivable(input: $input) {
      noteReceivable {
        ...NoteReceivableFragment
      }
    }
  }
`;

export const clientUpdateNoteReceivable = async (client: GraphQLClient, input: UpdateNoteReceivableInput) => {
  return (
    await client.request<Pick<Mutation, 'updateNoteReceivable'>>(updateNoteReceivableMutation, {
      input,
    })
  ).updateNoteReceivable;
};
